// Utility functions

/** Merge CSS class names, filtering falsy values */
export function cn(...classes: (string | false | null | undefined)[]): string {
  return classes.filter(Boolean).join(" ");
}

/** Format a number as a compact string (1.2K, 3.4M) */
export function formatCompact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toString();
}

/** Format cents as dollars */
export function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;
}

/** Truncate text to max length with ellipsis */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 1) + "\u2026";
}

/** Convert snake_case task slug to human-readable text */
export function humanizeSlug(slug: string): string {
  return slug
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/** Relative "time ago" label from a date string. Single implementation —
 *  delegates to `timeAgo` (declared below, hoisted) so the near-identical helpers
 *  don't drift (PR #293 review). */
export function relativeTime(dateStr: string): string {
  return timeAgo(dateStr);
}

/** Format duration in ms to human readable (e.g. "2m 5s", "45s", "1h 3m") */
export function formatDuration(ms: number | null): string {
  if (ms === null) return "—";
  if (ms < 1000) return `${ms}ms`;
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes < 60) return `${minutes}m ${remainingSeconds}s`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
}

/** Map BriefState to Badge variant */
export function briefStateVariant(state: string): "default" | "success" | "warning" | "error" | "info" {
  switch (state) {
    case "published":
    case "approved":
    case "scheduled":
      return "success";
    case "blocked":
    case "cancelled":
      return "error";
    case "in_review":
    case "voice_review":
      return "warning";
    default:
      return "info";
  }
}

/** Time ago (relative). Null-safe → "Unknown" for nullish/invalid dates so it can
 *  serve every caller (the canonical implementation; see `relativeTime`). */
export function timeAgo(date: string | Date | null | undefined): string {
  if (!date) return "Unknown";
  const ts = new Date(date).getTime();
  if (Number.isNaN(ts)) return "Unknown";
  const seconds = Math.floor((Date.now() - ts) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(date).toLocaleDateString();
}

/** Time until (relative future-focused) */
export function timeUntil(date: string | Date): string {
  const seconds = Math.floor(
    (new Date(date).getTime() - Date.now()) / 1000,
  );
  if (seconds <= 0) return "due now";
  if (seconds < 60) return "in under a minute";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `in ${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `in ${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `in ${days}d`;
  return new Date(date).toLocaleDateString();
}
